import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { usePostHog } from "posthog-js/react";
import { useCallback, useEffect, useMemo } from "react";

import { AskNobiButton } from "@/components/AskNobi/AskNobiButton";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { ErrorPage } from "@/components/Error/ErrorPage";
import { Settings01Icon } from "@/components/Icons/Settings01Icon";
import { DocumentPage } from "@/components/Page/DocumentPage";
import { Button } from "@/design-system/Button";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { Inline } from "@/design-system/Inline";
import { SIGNALR_EVENTS } from "@/helpers/signalr";
import { useProjectId } from "@/hooks/Network/useProjectId";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useBool } from "@/hooks/useBool";
import { usePermission } from "@/hooks/usePermission";
import { useSignalRHub, useSignalRSubscription } from "@/hooks/useSignalR";
import { useSlug } from "@/hooks/useSlug";
import { CreateChatModal } from "@/modules/chats/components/CreateChatModal";
import { useChatQueries } from "@/queries/chats/queryOptions";
import { QUERY_KEYS } from "@/query-keys";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

import { ChatListView } from "./components/ChatListView";

export function ChatListPage(): React.ReactNode {
  const { t } = useTranslation();
  const [isCreateChatModalOpened, createChatModalOpenHandler] = useBool(false);
  const hasPermission = usePermission();
  const slug = useSlug();
  const projectId = useProjectId();
  const sessionUser = useSessionUser();
  const queryClient = useQueryClient();
  const postHog = usePostHog();
  const chatQueries = useChatQueries();

  const {
    data: chatsData,
    hasNextPage: hasMoreChats,
    refetch: refetchChats,
    fetchNextPage: fetchMoreChats,
    isPending: isPendingChats,
    isFetchingNextPage: isFetchingMoreChats,
    error: chatsError,
  } = useInfiniteQuery(chatQueries.listInfinite());

  const onNewChatMessage = useCallback(async () => {
    await refetchChats();
  }, [refetchChats]);

  const { signalRConnection } = useSignalRHub("notification-hub", {
    query: `userId=${sessionUser.id}`,
  });
  useSignalRSubscription(signalRConnection, SIGNALR_EVENTS.CHAT.NEW_PROJECT_CHAT_MESSAGE_RECEIVED, onNewChatMessage);
  useSignalRSubscription(signalRConnection, SIGNALR_EVENTS.CHAT.NEW_PROJECT_GROUP_CHAT_CREATED, onNewChatMessage);

  const chats = useMemo(() => chatsData?.pages.flatMap((page) => page.items) ?? [], [chatsData]);

  useEffect(() => {
    chats.forEach((chat) => {
      if (chat.hasUnreadReply) {
        void queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.CHATS_REPLIES(projectId, chat.id),
        });
      }
    });
  }, [chats, projectId, queryClient]);

  if (chatsError) {
    return <ErrorPage error={chatsError} />;
  }

  const canCreateNewChat = sessionUser.chatPermission !== "disabled";

  return (
    <>
      <DocumentPage
        containerBoundary="inherit"
        tabTitle={t("page.chats.title")}
        breadcrumbs={
          <Breadcrumbs
            pages={[
              {
                name: sessionUser.project.name,
              },
              {
                name: t("page.chats.title"),
              },
            ]}
          />
        }
        actions={
          <Inline gap="sm" align="center">
            <Button
              href={`${routes.users.edit({ id: sessionUser.id, slug })}#chat-settings`}
              type="link"
              variant="soft"
              icon={<Settings01Icon />}
              onClick={() => postHog.capture("clicked_edit_profile_from_chat_list")}
            >
              {t("page.chats.edit-chat-settings")}
            </Button>
            <AskNobiButton
              defaultFilters={{ projectIds: [projectId] }}
              context={{ scope: "project", entity: "chats", page: "list" }}
              suggestions={[
                t("component.ask-nobi-chat-drawer.chats.suggestion.1"),
                t("component.ask-nobi-chat-drawer.chats.suggestion.2"),
              ]}
            />
            <Button
              data-testid="create-chat"
              disabled={!canCreateNewChat || !hasPermission((x) => x.userManagement.canListUsers)}
              onClick={createChatModalOpenHandler.setTrue}
            >
              {t("page.chats.new-chat")}
            </Button>
          </Inline>
        }
      >
        {isPendingChats ? (
          <FullSizeLoader />
        ) : (
          <ChatListView
            onLoadMoreChats={fetchMoreChats}
            hasMoreChats={hasMoreChats}
            chats={chats}
            isLoadingMoreChats={isFetchingMoreChats}
          />
        )}
      </DocumentPage>
      <CreateChatModal isOpened={isCreateChatModalOpened} onOpenChange={createChatModalOpenHandler.set} />
    </>
  );
}
